import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  type ReviewCriterionRow,
  useCreateReviewCriterion,
  useUpdateReviewCriterion,
} from "@/hooks/use-review-criteria";

/**
 * Write or edit one question — EPIC-045.
 *
 * Used from two places, which is why it takes a question rather than a mode:
 * Settings, and the form builder's "add a question" flow, where writing a new
 * one has to be possible without leaving the form.
 *
 * The competency link is offered last and described by what it does rather
 * than by what it is. "Link to a competency" means nothing to the person
 * writing a question; "show reviewers what this looks like at their level"
 * is the same setting explained.
 */
export function ReviewQuestionDialog({
  open,
  question,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  question: ReviewCriterionRow | null;
  onOpenChange: (open: boolean) => void;
  /** The new question's id AND title, so a caller can put it straight on a form without waiting for a refetch. */
  onCreated?: (id: string, title: string) => void;
}) {
  const create = useCreateReviewCriterion();
  const update = useUpdateReviewCriterion();
  const [title, setTitle] = useState("");
  const [helpText, setHelpText] = useState("");
  /** Null only until the admin picks one — a question can't be saved without a group (AC-419). */
  const [categoryId, setCategoryId] = useState<string | null>(null);
  /** Only flag the group as invalid once the admin has touched it — not the instant a blank dialog opens. */
  const [categoryTouched, setCategoryTouched] = useState(false);
  const [competencyId, setCompetencyId] = useState<string | null>(null);
  const [isDefault, setIsDefault] = useState(false);

  /**
   * Seeded ONCE PER OPEN, keyed on the question's id — never on the question
   * object.
   *
   * A background refetch (tabbing away and back) hands this a new object
   * reference for the same row, which re-ran the effect and blanked whatever
   * the admin had typed. Three sibling dialogs carry exactly this guard, with a
   * comment saying it exists to stop this.
   */
  const seededFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      seededFor.current = null;
      return;
    }
    const key = question?.id ?? "new";
    if (seededFor.current === key) return;
    seededFor.current = key;
    setTitle(question?.title ?? "");
    setHelpText(question?.helpText ?? "");
    setCategoryId(question?.categoryId ?? null);
    setCategoryTouched(false);
    setCompetencyId(question?.competencyId ?? null);
    setIsDefault(question?.isDefault ?? false);
  }, [open, question]);

  const pending = create.isPending || update.isPending;
  const ready = title.trim().length > 0 && categoryId !== null;

  function save() {
    if (categoryId === null) return;
    const input = {
      title: title.trim(),
      helpText: helpText.trim() || null,
      categoryId,
      competencyId,
      isDefault,
    };
    if (question) {
      update.mutate({ id: question.id, ...input }, { onSuccess: () => onOpenChange(false) });
      return;
    }
    create.mutate(input, {
      onSuccess: (result) => {
        const id = result?.data?.id;
        if (id && onCreated) onCreated(id, input.title);
        onOpenChange(false);
      },
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{question ? "Edit this question" : "Add a question"}</DialogTitle>
          <DialogDescription>
            Questions are shared across every review form, so wording it once means it reads the
            same wherever it is asked.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="question-title">What are reviewers asked?</Label>
            <Input
              id="question-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="Communicates clearly"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="question-help">Anything that helps them answer it (optional)</Label>
            <Textarea
              id="question-help"
              value={helpText}
              onChange={(event) => setHelpText(event.target.value)}
              placeholder="Explains trade-offs to people outside their own team."
              rows={2}
            />
          </div>

          <div className="grid gap-1.5">
            <Label>Group it under</Label>
            <EntityCombobox
              entity="categories"
              value={categoryId}
              onChange={(value) => {
                setCategoryId(value);
                setCategoryTouched(true);
              }}
              placeholder="Pick a group"
              aria-invalid={categoryTouched && categoryId === null}
            />
          </div>

          <div className="grid gap-1.5">
            <Label>Show reviewers what this looks like at the person's level (optional)</Label>
            <EntityCombobox
              entity="competencies"
              value={competencyId}
              onChange={setCompetencyId}
              filters={{ status: "active" }}
              includeNone
              placeholder="Don't show expectations"
            />
            <p className="text-2xs text-muted-foreground">
              Pick the competency this question is about, and reviewers see what meeting
              expectations looks like for that person's role and level. Leave it if the question
              doesn't map to one.
            </p>
          </div>

          <Label htmlFor="question-default" className="flex items-start gap-2 font-normal">
            <Checkbox
              id="question-default"
              checked={isDefault}
              onCheckedChange={(checked) => setIsDefault(checked === true)}
            />
            <span>
              Put this on new forms automatically
              <span className="block text-muted-foreground text-xs">
                New forms start with every standard question already added, in this order.
              </span>
            </span>
          </Label>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!ready || pending} onClick={save}>
            {pending ? "Saving…" : question ? "Save question" : "Add question"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
